import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { CustomButton, CustomPopover, Loader, SidePanel, Tabs } from 'components';
import {
  CalendarIcon,
  ChevronArrowRightIcon,
  CustomerIcon,
  DownArrowIcon,
  LocationIcon,
  NavCloseIcon,
  RedirectIcon
} from 'assets/icons';
import {
  useGetJobByIdQuery,
  useLazyGetJobTimelineQuery,
  useLazyGetProfitabilitySummaryQuery
} from 'containers/jobs/api';
import { useGetSuggestedAdjustmentsForJobQuery } from 'containers/accounting/pay-runs/api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import DateUtils from 'utils/dateUtils';
import { getAddressAsString } from 'containers/jobs/utils';
import JobReviewBillingTab from './JobReviewBillingTab';
import { ButtonType } from 'components/custom-button/CustomButton';
import { getJobReviewStatusMap } from '../utils';
import { JobReviewStatus, JobReviewTabs } from '../constants';
import { WarningIcon } from 'assets/icons';
import { PopoverArrowTypes } from 'constants/common';
import HistoryTab from 'containers/jobs/job-details/components/HistoryTab';
import { JobTimelineEvent, JobTimelineEventType } from 'containers/jobs/jobs.model';
import { useCreateNoteMutation, useDeleteNoteMutation } from 'containers/jobs/api';
import { API_RESPONSES } from 'types/baseTypes';
import { ImagePreview } from 'components';
import CreateEditNoteModal from 'containers/jobs/components/CreateEditNoteModal';
import { DeleteNoteConfirmationModal } from 'containers/jobs/components/DeleteNoteConfirmationModal';
import { useUpdateJobStatusMutation } from 'containers/accounting/pay-runs/confirm-jobs/api';
import { openInNewTab } from 'utils/commonUtils';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from 'containers/jobs/events';
import { PayRunsCleverTapEvents } from '../events';
import { Divider } from '@mui/material';

const JobReviewPanel: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSidePanelClose, jobNavigationFunctions } = useOutletContext<any>();
  const { jobId, payRunId, merchantId } = useParams();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { approveAndProcessAccounting } = useAppSelector(selectCurrentTeamAccountingPermissions);
  const [selectedTab, setSelectedTab] = useState<string>(JobReviewTabs.BILLING);
  const [isStatusPopoverOpen, setIsStatusPopoverOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<JobTimelineEvent | null>(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDeleteNoteConfirmation, setShowDeleteNoteConfirmation] = useState(false);
  const [notesInput, setNotesInput] = useState('');
  const [canNavigateNext, setCanNavigateNext] = useState(false);
  const [canNavigatePrev, setCanNavigatePrev] = useState(false);

  const jobReviewStatus = location.state?.jobReviewStatus || JobReviewStatus.IN_PROGRESS;

  const {
    data: jobData,
    isLoading,
    isError
  } = useGetJobByIdQuery({
    jobId,
    teamId: currentTeam.id,
    isFromEdit: true
  });
  const job = jobData?.data;
  const [getProfitabilitySummaryQuery, { data: profitabilityData, isFetching: isFetchingProfitability }] =
    useLazyGetProfitabilitySummaryQuery();

  const {
    data: suggestedAdjustments,
    isFetching: isFetchingSuggestedAdjustments,
    refetch: refetchSuggestedAdjustments
  } = useGetSuggestedAdjustmentsForJobQuery(
    {
      teamId: job?.teamId,
      jobId: job?.id
    },
    {
      skip: !job?.teamId || !job?.id
    }
  );
  const [getJobTimelineQuery, getJobTimelineState] = useLazyGetJobTimelineQuery();
  const { data: jobTimelineResponse } = getJobTimelineState;
  const [createNoteMutation, createNoteState] = useCreateNoteMutation();
  const { isSuccess: isCreateNoteSuccess, isLoading: isCreateNoteLoading } = createNoteState;
  const [deleteNoteMutation] = useDeleteNoteMutation();
  const timezone = DateUtils.getTimezoneLongName();
  const [updateJobStatus] = useUpdateJobStatusMutation();

  useEffect(() => {
    if (currentTeam?.id && jobId) {
      getProfitabilitySummaryQuery({
        teamId: currentTeam.id,
        jobId
      });
      getJobTimelineQuery({
        teamId: currentTeam.id,
        jobId,
        timezone
      });
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunJobReviewPanelView, {
        payRunId,
        merchantId,
        jobId
      });
    }
  }, [currentTeam?.id, jobId]);

  useEffect(() => {
    if (!isCreateNoteLoading && isCreateNoteSuccess) {
      refetchJobsTimeline();
    }
  }, [isCreateNoteSuccess, isCreateNoteLoading]);

  useEffect(() => {
    if (jobId && jobNavigationFunctions) {
      const nextJob = jobNavigationFunctions.getNextJob(jobId);
      const prevJob = jobNavigationFunctions.getPreviousJob(jobId);

      setCanNavigateNext(!!nextJob.jobId);
      setCanNavigatePrev(!!prevJob.jobId);
    }
  }, [jobId, jobNavigationFunctions]);

  useEffect(() => {
    if (selectedTab === JobReviewTabs.NOTES || selectedTab === JobReviewTabs.HISTORY) {
      refetchJobsTimeline();
    }
  }, [selectedTab]);

  const jobReviewStatusObject = useMemo(() => {
    return getJobReviewStatusMap(jobReviewStatus);
  }, [jobReviewStatus]);

  const handleGoToJobDetails = () => {
    window.open(`/jobs/${currentTeam.id}/${jobId}`, '_blank');
  };

  const NotesList = useMemo(() => {
    return (
      jobTimelineResponse?.data?.timelineList.filter(timeline => timeline.type === JobTimelineEventType.Note) || []
    );
  }, [jobTimelineResponse?.data?.timelineList]);

  const handleConfirmJob = async () => {
    try {
      await updateJobStatus({
        teamId: currentTeam?.id?.toString() || '',
        payRunId,
        merchantId,
        status: JobReviewStatus.CONFIRMED,
        jobId: jobId
      });

      handleSidePanelClose();
    } catch (error) {
      //error
    }
  };

  const handleStatusChange = async (status: JobReviewStatus) => {
    try {
      await updateJobStatus({
        teamId: currentTeam?.id?.toString() || '',
        payRunId,
        merchantId,
        status,
        jobId
      });

      handleSidePanelClose();
    } catch (error) {
      //error
    }
  };

  const handleGoToInvoice = () => {
    const invoiceId = job?.latestInvoice?.id;
    if (invoiceId && currentTeam?.id) {
      openInNewTab(`/invoices/${currentTeam.id}/${invoiceId}/invoice`);
    }
  };

  const handleGoToEstimate = () => {
    const estimateId = job?.estimates?.[0]?.id;
    if (estimateId && currentTeam?.id) {
      openInNewTab(`/estimates/${currentTeam.id}/${estimateId}/estimate`);
    }
  };

  const onPrevClick = () => {
    if (!canNavigatePrev || !jobNavigationFunctions) return;

    const prevJob = jobNavigationFunctions.getPreviousJob(jobId);
    if (prevJob.jobId) {
      navigate(`/accounting/pay-runs/${payRunId}/members/${merchantId}/jobs/${prevJob.jobId}`, {
        state: {
          ...location.state,
          jobReviewStatus: prevJob.status
        }
      });
    }
  };

  const onNextClick = () => {
    if (!canNavigateNext || !jobNavigationFunctions) return;

    const nextJob = jobNavigationFunctions.getNextJob(jobId);
    if (nextJob.jobId) {
      navigate(`/accounting/pay-runs/${payRunId}/members/${merchantId}/jobs/${nextJob.jobId}`, {
        state: {
          ...location.state,
          jobReviewStatus: nextJob.status
        }
      });
    }
  };

  const handleEditNote = (event: JobTimelineEvent) => {
    setCurrentNote(event);
    setShowNotesModal(true);
  };

  const handleAddNoteClick = () => {
    setCurrentNote(null);
    setShowNotesModal(true);
  };

  const handleDeleteNote = (event: JobTimelineEvent) => {
    setCurrentNote(event);
    setShowDeleteNoteConfirmation(true);
  };

  const handleCloseNotesModalClick = () => {
    setCurrentNote(null);
    setShowNotesModal(false);
  };

  const closePreview = () => {
    setCurrentNote(null);
    setCurrentImage(0);
    setShowImageViewer(false);
  };

  const showPreview = (index: number, event: JobTimelineEvent) => {
    setCurrentImage(index);
    setCurrentNote(event);
    setShowImageViewer(true);
    logAnalyticEvent(JobsCleverTapEvents.webImageViewerOpened, {
      source: 'job-review-panel'
    });
  };

  const handleCreateNote = async (note: string, clearInput: () => void) => {
    const response = await createNoteMutation({
      teamId: currentTeam.id,
      jobId: job.id,
      comment: note,
      attachments: []
    });

    if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
      clearInput();
      refetchJobsTimeline();
    }
  };

  const refetchJobsTimeline = () => {
    getJobTimelineQuery({
      teamId: currentTeam.id,
      jobId: job.id,
      timezone
    });
  };

  const tabsList = useMemo(
    () => [
      {
        id: JobReviewTabs.BILLING,
        title: JobReviewTabs.BILLING,
        icon:
          (suggestedAdjustments?.totalCount || 0) > 0 && approveAndProcessAccounting ? (
            <WarningIcon className="path-stroke-current h-5 w-4 text-pending-orange" />
          ) : null,
        iconPosition: 'right' as const
      },
      { id: JobReviewTabs.NOTES, title: JobReviewTabs.NOTES },
      { id: JobReviewTabs.HISTORY, title: JobReviewTabs.HISTORY }
    ],
    [suggestedAdjustments?.totalCount, approveAndProcessAccounting]
  );

  const handleCustomerClick = () => {
    openInNewTab(`/customers/${currentTeam.id}/${job.customerCurrent?.id}/customer`);
  };

  if (isError) {
    return (
      <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={true}>
        <div className="flex h-full w-[60vw] flex-col">
          <div className="flex items-center gap-2 px-6 py-4">
            <button onClick={handleSidePanelClose} className="text-primaryText">
              <NavCloseIcon className="h-4 w-4" />
            </button>
            <span className="ml-2 text-17px font-semibold text-headingGray">Job review</span>
          </div>
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <h2 className="mb-4 text-xl font-bold text-primaryText">Failed to load job data</h2>
            <p className="text-accentText">There was an error loading the job information. Please try again later.</p>
          </div>
        </div>
      </SidePanel>
    );
  }

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={true}>
      <div className="flex h-full w-[52vw] flex-col">
        {isLoading || !jobData?.data ? (
          <Loader />
        ) : (
          <>
            {/* Header Section */}
            <div className="flex flex-col justify-between gap-4 border-b border-secondaryBtn px-6 py-2">
              <div className="flex items-center gap-2">
                <button onClick={handleSidePanelClose} className="text-primaryText">
                  <NavCloseIcon className="h-4 w-4" />
                </button>
                <span className="ml-2 text-17px font-semibold text-headingGray">Job review</span>
                <div className="inline-block h-1 w-1 min-w-[4px] rounded-full bg-[#D9D9D9]"></div>
                <div className="text-17px font-semibold text-headingGray">{job.jobNumber.replaceAll('JOB-', '#')}</div>
                <div className="ml-auto flex items-center gap-2">
                  <CustomButton
                    id="prev-job"
                    onClick={onPrevClick}
                    type={ButtonType.SECONDARY}
                    className="h-[32px] px-3"
                    disabled={!canNavigatePrev}>
                    <div className="flex items-center gap-2">
                      <ChevronArrowRightIcon className="path path-fill-current h-4 w-4 rotate-180 text-secondary" />
                      Prev job
                    </div>
                  </CustomButton>
                  <CustomButton
                    id="next-job"
                    onClick={onNextClick}
                    type={ButtonType.SECONDARY}
                    className="h-[32px] px-3"
                    disabled={!canNavigateNext}>
                    <div className="flex items-center gap-2">
                      Next job
                      <ChevronArrowRightIcon className="path path-fill-current h-4 w-4 text-secondary" />
                    </div>
                  </CustomButton>
                </div>
              </div>
            </div>

            {/* Job Details Section */}
            <div className="flex border-b border-secondaryBtn bg-[#F8FBFF] px-6 py-2.5">
              <div className="w-1/2">
                <div className="flex items-center">
                  <h2 className="truncate text-xl font-bold text-headingGray">
                    {job.jobNumber.replaceAll('JOB-', '#')} - {job.title}
                  </h2>
                  <CustomButton
                    id="job-details-button"
                    type={null}
                    className="h-8 px-0 text-secondary"
                    onClick={handleGoToJobDetails}
                    EndIcon={RedirectIcon}
                    endIconStyle="w-[18px] h-[18px]"
                  />
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="text-sbase font-medium text-primaryText">Review status:</div>
                  <div
                    style={{
                      backgroundColor: jobReviewStatusObject.bg,
                      color: jobReviewStatusObject.text
                    }}
                    className="inline-flex w-fit items-center gap-2 rounded-md px-[10px] py-1
                   text-sbase font-bold capitalize">
                    {jobReviewStatusObject.label}
                  </div>
                </div>
              </div>
              <Divider orientation="vertical" flexItem className="mx-2 h-full xl:mx-6 2xl:mx-10" />
              <div className="w-1/2 space-y-[6.5px]">
                <div className="flex items-center gap-1 text-[14px] text-primaryText">
                  <CustomerIcon className="path path-fill-current mr-1 h-[18px] w-[18px] text-accentText" />
                  <span className="font-medium">{job.customerCurrent?.name}</span>
                  <CustomButton
                    id="customer-button"
                    type={null}
                    className="h-fit px-0 text-secondary"
                    onClick={handleCustomerClick}
                    EndIcon={RedirectIcon}
                    endIconStyle="w-4 h-4"
                  />
                </div>
                <div className="flex items-start gap-1 text-[14px] font-medium text-primaryText">
                  <CalendarIcon className="path path-fill-current mr-1 h-[18px] w-[18px] text-accentText" />
                  <span className="mt-0.5">
                    {DateUtils.getDateInFormat({
                      date: new Date(job.scheduledFrom),
                      dateFormat: 'MMM dd, yyyy, '
                    })}
                    {`${DateUtils.formatTime(job.scheduledFrom)} - ${DateUtils.formatTime(job.scheduledTo)}`}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-1 text-[14px] font-medium text-primaryText">
                  <div className="flex items-center gap-2">
                    <LocationIcon className="path path-fill-current mr-1 h-[18px] w-[18px] text-accentText" />
                    {getAddressAsString(job.customerCurrent?.serviceAddress[0]) || '-'}
                  </div>
                </div>
              </div>
            </div>
            {/* Tabs Section */}
            <div className="border-b border-borderGray">
              <Tabs
                tabs={tabsList}
                selectedTab={selectedTab}
                onTabSelect={tab => setSelectedTab(tab as JobReviewTabs)}
                className="px-6"
                tabStyle="!min-w-[136px] text-sbase font-bold pt-2.5 pb-1.5"
              />
            </div>

            {/* Tab Content */}
            <div className="grow overflow-auto">
              {selectedTab === JobReviewTabs.BILLING && (
                <JobReviewBillingTab
                  job={job}
                  profitabilityData={profitabilityData?.data}
                  onInvoiceClick={handleGoToInvoice}
                  onEstimateClick={handleGoToEstimate}
                  isLoading={isFetchingProfitability}
                  suggestedAdjustments={suggestedAdjustments}
                  isFetchingSuggestedAdjustments={isFetchingSuggestedAdjustments}
                  refetchSuggestedAdjustments={refetchSuggestedAdjustments}
                />
              )}
              {selectedTab === JobReviewTabs.NOTES && (
                <HistoryTab
                  jobTimeline={NotesList}
                  onEditNote={handleEditNote}
                  onAddNote={handleAddNoteClick}
                  onDeleteNote={handleDeleteNote}
                  showPreview={showPreview}
                  onCreateNote={handleCreateNote}
                  showFilterOptions={false}
                />
              )}
              {selectedTab === JobReviewTabs.HISTORY && (
                <HistoryTab
                  jobTimeline={jobTimelineResponse?.data?.timelineList || []}
                  onEditNote={handleEditNote}
                  onAddNote={handleAddNoteClick}
                  onDeleteNote={handleDeleteNote}
                  showPreview={showPreview}
                  onCreateNote={handleCreateNote}
                />
              )}
            </div>

            {/* Bottom Actions */}
            {approveAndProcessAccounting && jobReviewStatus !== JobReviewStatus.CONFIRMED && (
              <div className="flex w-full items-center border-t border-borderGray">
                <button
                  id="confirm-job"
                  onClick={handleConfirmJob}
                  className="flex h-[40px] w-1/2 items-center justify-center gap-2
                 bg-secondary text-sbase font-semibold text-white hover:bg-secondary/90">
                  Confirm job
                </button>

                <div className="w-1/2">
                  <CustomPopover
                    showOnTop={true}
                    show={isStatusPopoverOpen}
                    onTogglePopover={() => setIsStatusPopoverOpen(!isStatusPopoverOpen)}
                    onClosePopover={() => setIsStatusPopoverOpen(false)}
                    anchorComponent={({ isPanelOpen }) => (
                      <button
                        id="change-status"
                        className="flex h-[40px] w-full items-center justify-center gap-2
                         bg-secondaryBtn px-6 text-sbase font-semibold text-secondary hover:bg-secondaryBtn/90">
                        <span>Change status to</span>
                        <DownArrowIcon
                          className={`path path-stroke-current h-4 w-4 text-secondary transition-transform ${
                            isPanelOpen ? 'rotate-0' : 'rotate-180'
                          }`}
                        />
                      </button>
                    )}
                    showArrow={true}
                    arrowType={PopoverArrowTypes.BOTTOM}
                    arrowOffset="right-1/2"
                    offset="right-[20%]"
                    margin="bottom-[44px]"
                    width="w-[200px]"
                    zIndex="z-50"
                    padding="p-0"
                    containerClassName="!w-full">
                    <div className="py-1">
                      {Object.values(JobReviewStatus)
                        .filter(status => status !== jobReviewStatus)
                        .map(status => (
                          <button
                            key={status}
                            onClick={() => {
                              handleStatusChange(status);
                              setIsStatusPopoverOpen(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sbase text-primaryText hover:bg-secondaryBtn">
                            {getJobReviewStatusMap(status).label}
                          </button>
                        ))}
                    </div>
                  </CustomPopover>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showNotesModal && (
        <CreateEditNoteModal
          onClose={handleCloseNotesModalClick}
          isEdit={!!currentNote}
          currentNote={currentNote}
          teamId={currentTeam.id}
          jobId={job.id}
          getJobTimeline={refetchJobsTimeline}
          defaultNote={notesInput}
          setNotesInput={setNotesInput}
        />
      )}

      {showDeleteNoteConfirmation && (
        <DeleteNoteConfirmationModal
          handleDelete={() => {
            deleteNoteMutation({
              teamId: currentTeam.id,
              jobId: job.id,
              noteId: currentNote?.noteId
            });
          }}
          onCloseClick={() => {
            setCurrentNote(null);
            setShowDeleteNoteConfirmation(false);
          }}
          processingDelete={false}
        />
      )}

      {showImageViewer && (
        <ImagePreview onClose={closePreview} images={currentNote?.attachments} selectedIndex={currentImage} />
      )}
    </SidePanel>
  );
};

export default JobReviewPanel;
